/**
 * The parts of the inspector a module contributes to (MODULES §8.3).
 *
 * Core actions look exactly like module ones (M11): one select, one group per
 * source, the same description under it. The customer picks *what the button
 * does*, and where the code for it comes from is our problem, not theirs. When
 * the action they picked takes parameters, the form for them is generated from
 * the same manifest — so a release with a new module grows a new form here
 * without a line changing.
 */

import type { ReactNode } from "react";

import type { ModuleArg, ModuleOut } from "../api/types";
import { useI18n } from "../i18n";
import { localized } from "../i18n/localized";
import { Field } from "./fields";
import { moduleBlockOf, moduleRowChoices, permissionShortName } from "./modules";
import {
  ACTIONS,
  LIMITS,
  MODULE_ACTION,
  MODULE_BLOCK,
  STATUS_ROWS,
  isModuleRow,
  validateParams,
  type Action,
  type ModuleActionRef,
  type ModuleBlockRef,
  type RowRef,
} from "./spec";

/** `module:location:get_fix` — ids cannot hold a colon, so this cannot clash. */
const MODULE_OPTION = "module:";

function optionValue(action: Action): string {
  return action.type === MODULE_ACTION
    ? `${MODULE_OPTION}${action.module}:${action.action}`
    : action.type;
}

/**
 * What a module action asks for on the first tap, as one line: the 🔒 hint
 * §8.3 calls for. `prompts` names permissions of the module's own manifest,
 * so the wording is the same whichever module it is.
 */
export function actionHint(
  module: ModuleOut | undefined,
  actionId: string,
  lang: string,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const action = module?.actions?.[actionId];
  if (!action) return "";
  const description = localized(action.description, lang);
  const prompts = action.prompts ?? [];
  const asks = prompts.length
    ? t("design.moduleAsks", { permission: permissionShortName(prompts[0]) })
    : "";
  return [description, asks].filter(Boolean).join(" ");
}

/**
 * What a freshly placed action's `params` — or a block's `props` — start at
 * (MODULES §2.6, §2.11): the manifest's own `default`, and `https://` for a
 * required link so the customer types the interesting half rather than the
 * scheme.
 *
 * An argument with neither is left out — the document says what its author
 * wrote, and the phone fills the defaults from the manifest it carries — and a
 * schema with nothing to seed yields no key at all.
 */
export function seedParams(
  schema: Record<string, ModuleArg> | undefined,
): Record<string, unknown> | undefined {
  const seeded: Record<string, unknown> = {};
  for (const [name, spec] of Object.entries(schema ?? {})) {
    if (spec.default !== undefined) seeded[name] = spec.default;
    else if (spec.kind === "url" && spec.required) seeded[name] = "https://";
  }
  return Object.keys(seeded).length > 0 ? seeded : undefined;
}

/** The four-key action, with `params` only when there is something in it. */
function moduleAction(
  module: string,
  action: string,
  params: Record<string, unknown> | undefined,
): ModuleActionRef {
  const next: ModuleActionRef = { type: MODULE_ACTION, module, action };
  if (params && Object.keys(params).length > 0) next.params = params;
  return next;
}

/** The five-key block, with `props` only when there is something in it. */
function moduleBlock(
  id: string,
  module: string,
  block: string,
  props: Record<string, unknown> | undefined,
): ModuleBlockRef {
  const next: ModuleBlockRef = { id, type: MODULE_BLOCK, module, block };
  if (props && Object.keys(props).length > 0) next.props = props;
  return next;
}

/**
 * A button's action: the app's own list first, then a group per enabled
 * module. Picking a module action writes the `module` action and seeds the
 * parameters that action declares.
 */
export function ActionSelect({
  action,
  modules,
  onChange,
}: {
  action: Action;
  modules: ModuleOut[];
  onChange: (action: Action) => void;
}) {
  const { t, lang } = useI18n();
  const enabled = modules.filter((module) => module.enabled);
  const current =
    action.type === MODULE_ACTION
      ? modules.find((module) => module.id === action.module)
      : undefined;

  return (
    <>
      <Field label={t("design.action")}>
        <select
          value={optionValue(action)}
          onChange={(event) => {
            const value = event.target.value;
            if (value.startsWith(MODULE_OPTION)) {
              const [module, actionId] = value.slice(MODULE_OPTION.length).split(":");
              const picked = modules.find((entry) => entry.id === module)?.actions?.[actionId];
              onChange(moduleAction(module, actionId, seedParams(picked?.params)));
              return;
            }
            onChange(
              value === "open_url"
                ? { type: value, url: "url" in action ? action.url : "https://" }
                : ({ type: value } as Action),
            );
          }}
        >
          <optgroup label={t("design.actionGroup.core")}>
            {ACTIONS.map((option) => (
              <option key={option} value={option} title={t(`design.actionHint.${option}`)}>
                {t(`design.action.${option}`)}
              </option>
            ))}
          </optgroup>
          {enabled.map((module) => (
            <optgroup key={module.id} label={localized(module.name, lang, module.id)}>
              {Object.entries(module.actions ?? {}).map(([id, entry]) => (
                <option
                  key={id}
                  value={`${MODULE_OPTION}${module.id}:${id}`}
                  title={actionHint(module, id, lang, t)}
                >
                  {localized(entry.label, lang, id)}
                  {(entry.prompts?.length ?? 0) > 0 ? " 🔒" : ""}
                </option>
              ))}
            </optgroup>
          ))}
          {/* A design saved against a module that is now off — or against an
              action this release no longer describes — keeps what it says: the
              option has to exist for the select to show it (MODULES §8.6). */}
          {action.type === MODULE_ACTION &&
            !(current?.enabled && current.actions?.[action.action]) && (
              <optgroup label={t("design.actionGroup.off")}>
                <option value={optionValue(action)}>{`${action.module}.${action.action}`}</option>
              </optgroup>
            )}
        </select>
      </Field>
      <p className="muted small design-action-hint">
        {action.type === MODULE_ACTION
          ? actionHint(current, action.action, lang, t) ||
            t("design.moduleActionUnknown", { module: action.module })
          : t(`design.actionHint.${action.type}`)}
      </p>
    </>
  );
}

/** The per-instance parameters of the module action a button runs (MODULES
 *  §2.6, §8.3), as a form generated from the manifest (`ArgsForm`). */
export function ModuleParams({
  action,
  modules,
  onChange,
}: {
  action: ModuleActionRef;
  modules: ModuleOut[];
  onChange: (action: ModuleActionRef) => void;
}) {
  // Not `enabled`: a switched-off module's params are still checked, and
  // fixing them is what makes the button work once it goes back on.
  const declared = modules.find((entry) => entry.id === action.module)?.actions?.[action.action];
  return (
    <ArgsForm
      schema={declared?.params}
      values={action.params ?? {}}
      onChange={(params) => onChange(moduleAction(action.module, action.action, params))}
    />
  );
}

/**
 * The per-instance properties of a module *block* (MODULES §2.11, §7.4).
 *
 * `ModuleParams`' twin, and deliberately its twin rather than the same
 * component: the two read different halves of a manifest and write different
 * halves of a document. What they share — the form itself — is `ArgsForm`,
 * which is what keeps a `url` prop and a `url` param behaving identically.
 */
export function ModuleProps({
  block,
  modules,
  onChange,
}: {
  block: ModuleBlockRef;
  modules: ModuleOut[];
  onChange: (block: ModuleBlockRef) => void;
}) {
  const declared = moduleBlockOf(modules, block);
  return (
    <ArgsForm
      schema={declared?.props}
      values={block.props ?? {}}
      onChange={(props) => onChange(moduleBlock(block.id, block.module, block.block, props))}
    />
  );
}

/**
 * A form generated from the manifest's own arg specs, in the order it declares
 * them: every `kind` gets the control that fits it, bounded by the spec's own
 * limits, so the form cannot produce a document the validator refuses by
 * accident. When it does — a link pasted as `http://` — the sentence under the
 * field is the validator's own, the same one the sidebar shows against
 * `blocks[site].props.url`, so the two can never disagree.
 */
function ArgsForm({
  schema,
  values,
  onChange,
}: {
  schema: Record<string, ModuleArg> | undefined;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
}) {
  const { t, lang } = useI18n();
  if (!schema || Object.keys(schema).length === 0) return null;

  const problems = fieldMessages(schema, values);
  const set = (name: string, value: unknown) => {
    const next = { ...values };
    // A field a customer emptied is an *absent* argument rather than an empty
    // one: that is what leaving it out looks like to the server.
    if (value === undefined) delete next[name];
    else next[name] = value;
    onChange(next);
  };

  return (
    <div className="design-params">
      {Object.entries(schema).map(([name, spec]) => (
        <ParamField
          key={name}
          name={name}
          spec={spec}
          value={values[name]}
          error={problems[name]}
          lang={lang}
          requiredTitle={t("design.paramRequired")}
          onChange={(value) => set(name, value)}
        />
      ))}
    </div>
  );
}

/** The validator's own sentence per field — one function, two places it shows. */
function fieldMessages(
  schema: Record<string, ModuleArg>,
  values: Record<string, unknown>,
): Record<string, string> {
  const byName: Record<string, string> = {};
  for (const problem of validateParams(schema, values, "params")) {
    const name = problem.path.slice("params.".length);
    // An unknown parameter has no field to sit under; the sidebar has it.
    if (name in schema && !(name in byName)) byName[name] = problem.message;
  }
  return byName;
}

/** One parameter, rendered as its `kind` deserves (MODULES §2.8). */
function ParamField({
  name,
  spec,
  value,
  error,
  lang,
  requiredTitle,
  onChange,
}: {
  name: string;
  spec: ModuleArg;
  value: unknown;
  error?: string;
  lang: string;
  requiredTitle: string;
  onChange: (value: unknown) => void;
}) {
  // The manifest's label, and the id only when it has none to give (§2.8).
  const label = localized(spec.label, lang, name);
  const description = localized(spec.description, lang);
  const invalid = error ? "design-invalid" : undefined;
  const text = typeof value === "string" ? value : "";
  const wrap = (hint: string, control: ReactNode) => (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={spec.required}
      requiredTitle={requiredTitle}
    >
      {control}
    </Field>
  );

  if (spec.kind === "bool") {
    return wrap(
      description,
      <input
        type="checkbox"
        aria-label={label}
        checked={value === true}
        onChange={(event) => onChange(event.target.checked)}
      />,
    );
  }

  if (spec.kind === "enum") {
    return wrap(
      description,
      <select
        aria-label={label}
        className={invalid}
        value={text}
        onChange={(event) => onChange(event.target.value || undefined)}
      >
        {/* Blank while it is optional, and while a required one has not been
            answered — a select showing a choice nobody made is a lie. */}
        {(!spec.required || text === "") && <option value="" />}
        {(spec.choices ?? []).map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>,
    );
  }

  if (spec.kind === "int" || spec.kind === "float") {
    return wrap(
      description,
      <input
        type="number"
        aria-label={label}
        className={invalid}
        step={spec.kind === "float" ? "any" : 1}
        min={spec.min}
        max={spec.max}
        required={spec.required}
        value={typeof value === "number" ? String(value) : ""}
        onChange={(event) => {
          const next = Number(event.target.value);
          onChange(event.target.value === "" || !Number.isFinite(next) ? undefined : next);
        }}
      />,
    );
  }

  if (spec.kind === "url") {
    return wrap(
      description,
      <input
        type="url"
        inputMode="url"
        aria-label={label}
        className={invalid}
        placeholder="https://"
        maxLength={LIMITS.url}
        spellCheck={false}
        required={spec.required}
        value={text}
        onChange={(event) => onChange(event.target.value || undefined)}
      />,
    );
  }

  const max = spec.max ?? LIMITS.paramText;
  return wrap(
    [description, `${text.length}/${max}`].filter(Boolean).join(" · "),
    <input
      aria-label={label}
      className={invalid}
      maxLength={max}
      required={spec.required}
      value={text}
      onChange={(event) => onChange(event.target.value || undefined)}
    />,
  );
}

/**
 * The status block's rows: the core five, then every enabled module's values
 * that may be one. Nobody types `location.last_fix` — this is where it comes
 * from (MODULES §7.2).
 */
export function RowsPicker({
  rows,
  modules,
  onChange,
}: {
  rows: RowRef[];
  modules: ModuleOut[];
  onChange: (rows: RowRef[]) => void;
}) {
  const { t, lang } = useI18n();
  const choices = moduleRowChoices(modules, lang);
  // A row saved against a module that has since been switched off is still on
  // the screen, so it is still in the list — with its raw id, all we have.
  const orphans = rows.filter(
    (row) => isModuleRow(row) && !choices.some((choice) => choice.row === row),
  );

  const toggle = (row: RowRef, on: boolean) =>
    onChange(on ? [...rows, row] : rows.filter((entry) => entry !== row));

  return (
    <div className="design-rows">
      <span className="design-field-label">{t("design.rows")}</span>
      {STATUS_ROWS.map((row) => (
        <label key={row} className="design-row-choice">
          <input
            type="checkbox"
            checked={rows.includes(row)}
            onChange={(event) => toggle(row, event.target.checked)}
          />
          <span>{t(`design.row.${row}`)}</span>
        </label>
      ))}
      {choices.map((choice) => (
        <label key={choice.row} className="design-row-choice">
          <input
            type="checkbox"
            checked={rows.includes(choice.row)}
            onChange={(event) => toggle(choice.row, event.target.checked)}
          />
          <span>
            {choice.label}
            <span className="muted small mono"> {choice.row}</span>
          </span>
        </label>
      ))}
      {orphans.map((row) => (
        <label key={row} className="design-row-choice">
          <input type="checkbox" checked onChange={() => toggle(row, false)} />
          <span className="mono small">{row}</span>
        </label>
      ))}
    </div>
  );
}
